import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback } from 'react';
import { artifactExportService, ExportFormat } from '../services/artifactExportService';
import { demoProjectsService } from '../services/demoProjectsService';
import { useProjectData } from './ProjectDataContext';
import { useUserProjectsContext } from './UserProjectsContext';
import { isContourName, isContourBlockedAtLowRes, parseContourInterval } from '../hooks/useLayerTypeDetection';
import { isLiveDemo, isScouting } from '../utils/capabilities';
import { eventBus } from '../events';
const ArtifactActionsContext = createContext(undefined);
export const ArtifactActionsProvider = ({ children, onLoadArtifact, onToggleArtifact, onUpdateArtifactOpacity, onUpdateArtifactWeight, onUpdateArtifactMonochrome, onHideAllArtifacts, onAddStatus }) => {
    // Get current project context
    const { selectedProject, artifacts } = useProjectData();
    const { projects: userProjects } = useUserProjectsContext();
    // Export state management
    const [exportState, setExportState] = useState({
        isExporting: new Map(),
        lastExportResults: new Map()
    });
    // Gate fine-interval contours on low-res scouting projects. An expired demo
    // (or a locked project) is served the low-res scouting set, where only the
    // coarsest contour intervals are meaningful. Showing a finer interval is
    // intercepted before the layer loads and opens the data-quality explainer
    // instead. Returns true when the request was gated (caller must not proceed).
    // Covers BOTH show paths: onLoadArtifact (first show, not yet loaded) and
    // onToggleArtifact (re-show once loaded) — FolderTree picks between them.
    // The currently-selected project IF it is served in low-res "scouting" mode,
    // else null. Reads the gate's own verdict (accessLevel === 'scouting') rather
    // than re-deriving it from a status string. accessLevel is a fetch-time
    // snapshot, so we still refine one edge: a live demo that has ticked past its
    // expiry since the last fetch is already low-res in reality.
    const selectedLowResProject = useCallback(() => {
        var _a;
        const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
        const up = projectId ? userProjects.find(p => p.id === projectId) : undefined;
        if (!up)
            return null;
        const expiredSinceFetch = isLiveDemo(up.accessLevel) && up.demoExpiresAt != null && Date.now() / 1000 >= up.demoExpiresAt;
        const isLowRes = isScouting(up.accessLevel) || expiredSinceFetch;
        return isLowRes ? up : null;
    }, [selectedProject, userProjects]);
    const maybeGateContour = useCallback((name) => {
        if (!name || !isContourName(name) || !isContourBlockedAtLowRes(name))
            return false;
        const up = selectedLowResProject();
        if (!up)
            return false;
        const parsed = parseContourInterval(name);
        eventBus.emit('contours:lowResGated', {
            intervalLabel: parsed ? `${parsed.value} ${parsed.unit}` : name,
            projectId: up.id,
            projectName: up.name,
            demoExpiresAt: up.demoExpiresAt,
        });
        return true;
    }, [selectedLowResProject]);
    // Gate exports/downloads on low-res scouting projects — full-resolution data and
    // exports require a paid unlock (the backend denies exports for non-full access).
    // Open the unlock modal instead of attempting a download that would fail.
    //
    // Preview / unbuilt / restoring projects are a different case: the owner
    // already has full access, there is nothing to pay for, there is simply no
    // full-resolution set to export yet. Those get an informational notice, never
    // the payment modal. The server refuses them too; this only saves the round trip.
    const maybeGateExport = useCallback(() => {
        var _a;
        const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
        const owned = projectId ? userProjects.find(p => p.id === projectId) : undefined;
        // The preview set itself is being regenerated, so there is nothing at all to
        // export yet. Its own line: offering a build here would name an action the
        // server is already performing.
        if (owned && owned.displayStatus === 'restoring_preview') {
            onAddStatus('This project is restoring its preview. Exports will be available once it is rebuilt at full resolution.');
            return true;
        }
        if (owned && (owned.displayStatus === 'preview' || owned.displayStatus === 'unbuilt')) {
            onAddStatus('This project is at preview resolution. Build it at full resolution first, included while your Atlas plan is active.');
            return true;
        }
        const up = selectedLowResProject();
        if (!up)
            return false;
        eventBus.emit('project:openUnlockModal', {
            projectId: up.id,
            projectName: up.name,
            demoExpiresAt: up.demoExpiresAt,
        });
        return true;
    }, [selectedProject, userProjects, onAddStatus, selectedLowResProject]);
    const handleLoadArtifact = useCallback((artifactId, metadata) => {
        var _a, _b;
        const name = (_b = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.getName) === null || _a === void 0 ? void 0 : _a.call(metadata)) !== null && _b !== void 0 ? _b : '';
        if (maybeGateContour(name))
            return;
        onLoadArtifact(artifactId, metadata);
    }, [onLoadArtifact, maybeGateContour]);
    const handleToggleArtifact = useCallback((artifactId, visible) => {
        var _a, _b, _c;
        if (visible) {
            const name = (_c = (_b = (_a = artifacts.find(a => { var _a; return ((_a = a.getId) === null || _a === void 0 ? void 0 : _a.call(a)) === artifactId; })) === null || _a === void 0 ? void 0 : _a.getName) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : '';
            if (maybeGateContour(name))
                return;
        }
        onToggleArtifact(artifactId, visible);
    }, [onToggleArtifact, artifacts, maybeGateContour]);
    // Export artifact to specified format
    const onExportArtifact = useCallback(async (artifactId, format, isDemoProject = false) => {
        // Low-res scouting projects can't export — full-res data + exports need a paid
        // unlock. Open the unlock modal instead of a download that the server denies.
        if (maybeGateExport())
            return;
        try {
            // Set exporting state
            setExportState(prev => ({
                ...prev,
                isExporting: new Map(prev.isExporting).set(artifactId, true)
            }));
            onAddStatus(`🔄 Exporting artifact to ${artifactExportService.getFormatName(format)}...`);
            // Call export service
            const result = await artifactExportService.exportArtifact(artifactId, format, {}, isDemoProject);
            // Update state with result
            setExportState(prev => ({
                ...prev,
                isExporting: new Map(prev.isExporting).set(artifactId, false),
                lastExportResults: new Map(prev.lastExportResults).set(artifactId, result)
            }));
            if (result.success && result.downloadUrl) {
                onAddStatus(`✅ Export successful! Download starting...`);
                // Initiate download
                artifactExportService.initiateDownload(result);
            }
            else {
                onAddStatus(`❌ Export failed: ${result.message}`);
            }
        }
        catch (error) {
            // Clear exporting state on error
            setExportState(prev => ({
                ...prev,
                isExporting: new Map(prev.isExporting).set(artifactId, false)
            }));
            // Check if this is an authentication error that should be handled by the context menu
            if (error && typeof error === 'object' && 'code' in error &&
                (error.code === 16 || error.code === 7)) { // UNAUTHENTICATED or PERMISSION_DENIED
                // Re-throw authentication/permission errors so context menu can handle them
                throw error;
            }
            // Handle other errors generically
            const errorMessage = error instanceof Error ? error.message : 'Unknown export error';
            onAddStatus(`❌ Export failed: ${errorMessage}`);
            console.error('Export error:', error);
        }
    }, [onAddStatus, maybeGateExport]);
    // Get supported formats for artifact
    const onGetSupportedFormats = useCallback(async (artifactId, isDemoProject, artifactMetadata) => {
        var _a;
        try {
            // The backend needs projectId and layerName, not the artifact database ID
            // We need to extract these from the context and artifact metadata
            if (!artifactMetadata) {
                console.error(`Artifact metadata is required for getSupportedFormats`);
                return [];
            }
            // Get project ID from the current selected project context
            const projectId = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
            if (!projectId) {
                console.error(`No project currently selected - cannot determine project ID for getSupportedFormats`);
                return [];
            }
            // Extract layer name from object key in metadata JSON
            let layerName = '';
            try {
                const metadataJson = artifactMetadata.getMetadata();
                const metadata = JSON.parse(metadataJson || '{}');
                const objectKey = metadata.object_key;
                if (objectKey) {
                    // Extract filename from object key path
                    const lastSlashIndex = objectKey.lastIndexOf('/');
                    if (lastSlashIndex >= 0) {
                        const filename = objectKey.substring(lastSlashIndex + 1);
                        // Remove file extension
                        const dotIndex = filename.lastIndexOf('.');
                        layerName = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
                        // Strip _display suffix if present (matches backend logic)
                        if (layerName.endsWith('_display')) {
                            layerName = layerName.substring(0, layerName.length - '_display'.length);
                        }
                    }
                }
            }
            catch (e) {
                console.warn('Failed to parse artifact metadata JSON:', e);
            }
            // Fallback to using artifact name if object key extraction failed
            if (!layerName) {
                const artifactName = artifactMetadata.getName();
                const dotIndex = artifactName.lastIndexOf('.');
                layerName = dotIndex > 0 ? artifactName.substring(0, dotIndex) : artifactName;
            }
            // Route to correct service based on project type
            let result;
            if (isDemoProject) {
                // Use demo projects service
                const response = await demoProjectsService.getSupportedExportFormats(projectId, layerName);
                const availableFormats = response.getAvailableFormatsList();
                result = {
                    success: true,
                    availableFormats: availableFormats.map((af) => ({
                        format: af.getFormat(),
                        artifactId: af.getArtifactId()
                    }))
                };
            }
            else {
                // Use user projects service
                result = await artifactExportService.getSupportedExportFormats(projectId, layerName);
            }
            // Filter out UNSPECIFIED formats
            const filteredFormats = result.availableFormats.filter((af) => af.format !== ExportFormat.EXPORT_FORMAT_UNSPECIFIED);
            return filteredFormats;
        }
        catch (error) {
            // Extract variables from context since they may be out of scope
            let extractedProjectId = 'unknown';
            let extractedLayerName = 'unknown';
            try {
                const folderId = artifactMetadata === null || artifactMetadata === void 0 ? void 0 : artifactMetadata.getFolderId();
                if (folderId) {
                    extractedProjectId = folderId.split(':')[0] || 'unknown';
                }
                const metadataJson = artifactMetadata === null || artifactMetadata === void 0 ? void 0 : artifactMetadata.getMetadata();
                if (metadataJson) {
                    const metadata = JSON.parse(metadataJson || '{}');
                    const objectKey = metadata.object_key;
                    if (objectKey) {
                        const lastSlashIndex = objectKey.lastIndexOf('/');
                        if (lastSlashIndex >= 0) {
                            const filename = objectKey.substring(lastSlashIndex + 1);
                            const dotIndex = filename.lastIndexOf('.');
                            extractedLayerName = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
                        }
                    }
                }
            }
            catch (e) {
                // Ignore extraction errors in error handler
            }
            console.error('Frontend Error getting supported formats:', {
                artifactId,
                projectId: extractedProjectId,
                layerName: extractedLayerName,
                isDemoProject,
                error: error instanceof Error ? error.message : error,
                stack: error instanceof Error ? error.stack : undefined
            });
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            onAddStatus(`Failed to get export formats: ${errorMsg}`);
            // Return empty array on error - no mocks
            return [];
        }
    }, [selectedProject, onAddStatus]);
    // Check if artifact is currently exporting
    const isArtifactExporting = useCallback((artifactId) => {
        return exportState.isExporting.get(artifactId) || false;
    }, [exportState.isExporting]);
    // Get last export result for artifact
    const getLastExportResult = useCallback((artifactId) => {
        return exportState.lastExportResults.get(artifactId);
    }, [exportState.lastExportResults]);
    const value = {
        onLoadArtifact: handleLoadArtifact,
        onToggleArtifact: handleToggleArtifact,
        onUpdateArtifactOpacity,
        onUpdateArtifactWeight,
        onUpdateArtifactMonochrome,
        onHideAllArtifacts,
        onExportArtifact,
        onGetSupportedFormats,
        isArtifactExporting,
        getLastExportResult
    };
    return (_jsx(ArtifactActionsContext.Provider, { value: value, children: children }));
};
export const useArtifactActions = () => {
    const context = useContext(ArtifactActionsContext);
    if (context === undefined) {
        throw new Error('useArtifactActions must be used within an ArtifactActionsProvider');
    }
    return context;
};
